import { Disclosure } from "@headlessui/react"
import { ChevronUpIcon } from "@heroicons/react/20/solid"
import { Button, Input } from "@biteship/atom";
import Select from "@components/input/Select";
import {
  COLLECTION_METHOD_LABELS,
  COLLECTION_METHOD_VALUES,
  SERVICE_TYPES_LABELS,
  SERVICE_TYPES_VALUES,
  SHIPPING_TYPE_LABELS,
  SHIPPING_TYPE_VALUES,
  SHIPMENT_DURATION_UNIT_LABELS,
  SHIPMENT_DURATION_UNIT_VALUES,
} from "../constants";
import { useEffect, useMemo, useState } from "react";
import { updateOpenCourierService, deleteOpenCourierService } from "@store/action/openCourier";

/**
 * 
 * @param {Object} props
 * @param {number} props.index - index of the service
 * @param {string} props.openCourierId - open courier id
 * @param {import("@store/action/openCourier").OpenCourierService} props.service
 * @param {function} props.onUpdateService - function to update the service
 * @param {function} props.onDeleteService - function to delete the service
 * @returns 
 */
export default function ServiceAccordion({ index, openCourierId, service, onUpdateService, onDeleteService }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftService, setDraftService] = useState(service);

  useEffect(() => {
    setDraftService(service);
  }, [service]);

  const makeOptions = (values, labels) => values.map((v) => ({ value: v, label: labels[v] || v }));

  const collectionMethodOptions = useMemo(
    () => makeOptions(COLLECTION_METHOD_VALUES, COLLECTION_METHOD_LABELS),
    []
  );
  const serviceTypeOptions = useMemo(
    () => makeOptions(SERVICE_TYPES_VALUES, SERVICE_TYPES_LABELS),
    []
  );
  const shippingTypeOptions = useMemo(
    () => makeOptions(SHIPPING_TYPE_VALUES, SHIPPING_TYPE_LABELS),
    []
  );
  const durationUnitOptions = useMemo(
    () => makeOptions(SHIPMENT_DURATION_UNIT_VALUES, SHIPMENT_DURATION_UNIT_LABELS),
    []
  );

  const handleFieldChange = (field) => (e) => {
    const value = e?.target?.value;
    setDraftService((prev) => ({ ...prev, [field]: value }));
  };
  const handleCodeChange = (e) => {
    const code = e?.target?.value;
    // remove all spaces and special characters except underscore
    const sanitized = code.replace(/[^a-zA-Z0-9_]/g, "");
    setDraftService((prev) => ({ ...prev, serviceCode: sanitized }));
  };

  const handleShipmentDurationRangeChange = (e) => {
    const raw = e?.target?.value || "";
    const sanitized = raw.replace(/\s+/g, "").replace(/[^0-9-]/g, "");
    setDraftService((prev) => ({ ...prev, shipmentDurationRange: sanitized }));
  };

  const handleCollectionMethodChange = (idx) => (e) => {
    const value = e?.target?.value;
    setDraftService((prev) => {
      const next = Array.isArray(prev.collectionMethods) ? [...prev.collectionMethods] : [];
      next[idx] = value;
      return { ...prev, collectionMethods: next };
    });
  };

  const handleAddCollectionMethod = () => {
    setDraftService((prev) => {
      const current = Array.isArray(prev.collectionMethods) ? prev.collectionMethods : [];
      if (current.length >= COLLECTION_METHOD_VALUES.length) return prev;
      const available = COLLECTION_METHOD_VALUES.find((v) => !current.includes(v)) || COLLECTION_METHOD_VALUES[0];
      return { ...prev, collectionMethods: [...current, available] };
    });
  };

  const handleRemoveCollectionMethod = (idx) => () => {
    if (!isEditing) return;
    setDraftService((prev) => {
      const current = Array.isArray(prev.collectionMethods) ? [...prev.collectionMethods] : [];
      current.splice(idx, 1);
      return { ...prev, collectionMethods: current };
    });
  };

  const enterEdit = () => setIsEditing(true);
  const cancelEdit = () => {
    setDraftService(service);
    setIsEditing(false);
  };
  const saveEdit = async () => {
    if (!window.confirm("Simpan perubahan layanan ini?")) return;
    try {
      await updateOpenCourierService(openCourierId, service.id, draftService);
      alert("Layanan berhasil diperbarui");
      onUpdateService();
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating service:", error);
      const errorMessage = error?.response?.data?.error || "Gagal memperbarui layanan. Silahkan coba kembali";
      alert(errorMessage);
    }
  };
  const deleteService = async () => {
    if (!window.confirm("Hapus layanan ini?")) return;
    try {
      await deleteOpenCourierService(openCourierId, service.id);
      alert("Layanan berhasil dihapus");
      onDeleteService();
    } catch (error) {
      console.error("Error deleting service:", error);
      const errorMessage = error?.response?.data?.error || "Gagal menghapus layanan. Silahkan coba kembali";
      alert(errorMessage);
    }
  };

  const disabled = !isEditing;

  return (
    <div className="w-full rounded-lg bg-white border border-solid border-gray-300 mb-6 ">
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button
              className="flex w-full justify-between items-center rounded-lg px-4 py-3 text-left text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75"
            >
              <h3 className="text-gray-900">{index + 1}. {service.name}</h3>
              <ChevronUpIcon
                className={`${open ? "rotate-180 transform" : ""} h-5 w-5 text-gray-900`}
              />
            </Disclosure.Button>
            <Disclosure.Panel
              className="border-t border-gray-300"
            >
              <div className='flex flex-col w-full overflow-y-auto' style={{ maxHeight: '400px' }}>
                <div
                  className="flex justify-between items-center py-3 px-4 w-full space-x-4"
                  style={{ backgroundColor: isEditing ? "#eab30822" : "transparent" }}
                >
                  { isEditing ? <span style={{ color: "#eab308" }}>Anda sedang dalam mode edit</span> : <div></div> }
                  <div className="flex justify-end items-center space-x-4">
                    {isEditing ? (
                      <>
                        <Button title="Batal" className="md:w-auto" onClick={cancelEdit} secondary />
                        <Button title="Simpan" className="md:w-auto" onClick={saveEdit} />
                      </>
                    ) : (
                      <>
                        <Button title="Hapus" className="md:w-auto" onClick={deleteService} secondary />
                        <Button title="Ubah" className="md:w-auto" onClick={enterEdit} />
                      </>
                    )}
                  </div>
                </div>

                {/* Name */}
                <div className="flex space-x-2 items-center py-3 px-4 w-full border-t border-gray-300">
                  <span className='text-gray-700 w-1/3'>Nama Layanan</span>
                  <Input 
                    className={`w-full ${isEditing ? "text-gray-900" : ""}`} 
                    placeholder="Instant Bike"
                    labelHidden 
                    inputClassName="border-gray-300" 
                    value={draftService.name || ""} 
                    onChange={!isEditing ? () => {} : handleFieldChange("name")} 
                    disabled={disabled} 
                  />
                </div>

                {/* Service Code */}
                <div className="flex space-x-2 items-center py-3 px-4 w-full border-t border-gray-300">
                  <span className='text-gray-700 w-1/3'>Kode Layanan</span>
                  <div className='w-full'>
                    <Input 
                      className={`w-full ${isEditing ? "text-gray-900" : ""}`} 
                      labelHidden 
                      placeholder="instant_bike"
                      inputClassName="border-gray-300" 
                      value={draftService.serviceCode || ""} 
                      onChange={!isEditing ? () => {} : handleCodeChange} 
                      disabled={disabled} 
                    />
                    <p className="text-xs text-gray-700 mt-1 ml-1 mb-0">Kode layanan harus unik, hanya alfabet, angka, dan underscore, tanpa ada spasi. Contoh: "instant_bike", "reg", dll.</p>
                  </div>
                </div>

                {/* Description */}
                <div className="flex space-x-2 items-center py-3 px-4 w-full border-t border-gray-300">
                  <span className='text-gray-700 w-1/3'>Deskripsi</span>
                  <Input 
                    className={`w-full ${isEditing ? "text-gray-900" : ""}`} 
                    labelHidden 
                    placeholder="Deskripsi layanan Anda"
                    inputClassName="border-gray-300" 
                    value={draftService.description || ""} 
                    onChange={!isEditing ? () => {} : handleFieldChange("description")} 
                    disabled={disabled} 
                  />
                </div>

                {/* Shipping Type */}
                <div className="flex space-x-2 items-center py-3 px-4 w-full border-t border-gray-300">
                  <span className='text-gray-700 w-1/3'>Tipe Pengiriman</span>
                  <div className='w-full'>
                    <Select
                      labelHidden
                      className='w-full'
                      selectClassName="w-full"
                      options={shippingTypeOptions}
                      value={draftService.shippingType || ""}
                      onChange={handleFieldChange("shippingType")}
                      disabled={disabled}
                    />
                  </div>
                </div>

                {/* Service Type */}
                <div className="flex space-x-2 items-center py-3 px-4 w-full border-t border-gray-300">
                  <span className='text-gray-700 w-1/3'>Tipe Layanan</span>
                  <div className='w-full'>
                    <Select
                      labelHidden
                      className='w-full'
                      selectClassName="w-full"
                      options={serviceTypeOptions}
                      value={draftService.serviceType || ""}
                      onChange={handleFieldChange("serviceType")}
                      disabled={disabled}
                    />
                  </div>
                </div>

                {/* Shipment Duration Range (no spaces, digits and '-') */}
                <div className="flex space-x-2 items-center py-3 px-4 w-full border-t border-gray-300">
                  <span className='text-gray-700 w-1/3'>Rentang Durasi Pengiriman</span>
                  <div className='w-full'>
                    <Input 
                      className={`w-full ${isEditing ? "text-gray-900" : ""}`} 
                      labelHidden 
                      inputClassName="border-gray-300" 
                      value={draftService.shipmentDurationRange || ""} 
                      onChange={!isEditing ? () => {} : handleShipmentDurationRangeChange} 
                      disabled={disabled} 
                    />
                    <p className="text-xs text-gray-700 mt-1 ml-1 mb-0">Rentang durasi pengiriman harus berupa angka, tanpa ada spasi. Contoh: "1-3", "4", dll.</p>
                  </div>
                </div>

                {/* Shipment Duration Unit */}
                <div className="flex space-x-2 items-center py-3 px-4 w-full border-t border-gray-300">
                  <span className='text-gray-700 w-1/3'>Satuan Durasi Pengiriman</span>
                  <div className='w-full'>
                    <Select
                      labelHidden
                      options={durationUnitOptions}
                      value={draftService.shipmentDurationUnit || ""}
                      onChange={handleFieldChange("shipmentDurationUnit")}
                      disabled={disabled}
                    />
                  </div>
                </div>

                {/* Collection Methods */}
                <div className="flex space-x-2 items-start py-3 px-4 w-full border-t border-gray-300">
                  <span className='text-gray-700 w-1/3 pt-2'>Pengambilan Paket</span>
                  <div className='w-full'>
                    <div className='space-y-2'>
                      {(Array.isArray(draftService.collectionMethods) ? draftService.collectionMethods : []).map((cm, idx) => (
                        <div key={`cm-${idx}`} className="flex items-center space-x-2">
                          <div className='flex-1'>
                            <Select
                              labelHidden
                              options={collectionMethodOptions}
                              value={cm || ""}
                              onChange={handleCollectionMethodChange(idx)}
                              disabled={disabled}
                            />
                          </div>
                          {isEditing ? (
                            <Button title="Hapus" className="md:w-auto px-3" onClick={handleRemoveCollectionMethod(idx)} secondary />
                          ) : null}
                        </div>
                      ))}
                    </div>
                    {isEditing ? (
                      <div className='mt-2'>
                        <Button
                          title="Tambah"
                          className="md:w-auto"
                          onClick={handleAddCollectionMethod}
                          disabled={(Array.isArray(draftService.collectionMethods) ? draftService.collectionMethods.length : 0) >= COLLECTION_METHOD_VALUES.length}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>

              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  )
}